import React, { useEffect, useState } from 'react';
import { api } from '../api/client';
import useRefreshOnFocus from '../useRefreshOnFocus';
import CashEntryModal from '../components/CashEntryModal';
import { DeleteIcon, WarningIcon } from '../components/Icons';

const TYPES = ['401k', 'ROTH', '401k_Rollover', 'Investment', 'Other'];
const INSTITUTIONS = ['Merrill', 'Fidelity', 'Other'];
const today = () => new Date().toISOString().split('T')[0];
const EMPTY_FORM = { name: '', account_type: '401k', institution: 'Fidelity', opening_cash: '', opening_cash_date: today() };

export default function Accounts({ onNavigate }) {
  const [accounts, setAccounts] = useState([]);
  const [totals, setTotals] = useState({});
  const [cashBalances, setCashBalances] = useState({});
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [openingFor, setOpeningFor] = useState(null);
  const [formError, setFormError] = useState('');

  const load = async () => {
    const [accs, pos, cash] = await Promise.all([api.getAccounts(), api.getPositions(), api.getCashBalances()]);
    setAccounts(accs);
    setCashBalances(cash);
    const priceMap = {};
    await Promise.all([...new Set(pos.map(p => p.symbol))].map(async sym => {
      try { priceMap[sym] = (await api.getLatestPrice(sym)).close_price; } catch {}
    }));
    // Same basis as Dashboard: unpriced positions count at cost.
    const byAccount = {};
    pos.forEach(p => {
      const t = byAccount[p.account_id] || (byAccount[p.account_id] = { value: 0, cost: 0 });
      const cur = priceMap[p.symbol];
      t.cost += p.total_cost;
      t.value += cur ? cur * p.quantity : p.total_cost;
    });
    setTotals(byAccount);
  };

  useEffect(() => { load(); }, []);
  useRefreshOnFocus(load);

  const fmt = (n) => '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const fmtSigned = (n) => (n < 0 ? '-' : '') + fmt(Math.abs(n));
  const fmtPct = (n) => (n >= 0 ? '+' : '') + n.toFixed(2) + '%';

  const submit = async () => {
    const opening = parseFloat(form.opening_cash);
    if (!form.name.trim()) return setFormError('Enter an account name.');
    // Required, so the account can take transactions; $0.00 is a valid opening balance.
    if (!(opening >= 0)) return setFormError('Enter the opening cash balance (0 if the account starts empty).');
    if (!form.opening_cash_date) return setFormError('Enter the opening balance date.');
    try {
      await api.createAccount({
        name: form.name,
        account_type: form.account_type,
        institution: form.institution,
        opening_cash: opening,
        opening_cash_date: form.opening_cash_date,
      });
    } catch (e) {
      return setFormError(e.message);
    }
    setFormError('');
    setShowModal(false);
    setForm(EMPTY_FORM);
    load();
  };

  const remove = async (id) => {
    if (window.confirm('Delete this account? Its cash entries will be deleted too.')) {
      await api.deleteAccount(id);
      load();
    }
  };

  const openingSuggestion = openingFor && cashBalances[openingFor.id]?.suggested_opening_balance;

  return (
    <div>
      <div className="page-header">
        <h1 className="page-title">Accounts</h1>
        <button className="btn" onClick={() => { setFormError(''); setShowModal(true); }}>+ Add Account</button>
      </div>

      <div className="card">
        <table>
          <thead>
            <tr><th>Name</th><th>Type</th><th>Institution</th><th style={{textAlign:'right'}}>Cash</th><th style={{textAlign:'right'}}>Value</th><th></th></tr>
          </thead>
          <tbody>
            {accounts.map(a => {
              const t = totals[a.id];
              const c = cashBalances[a.id];
              const cash = c ? c.balance : 0;
              const gain = t ? t.value - t.cost : 0;
              const pct = t && t.cost > 0 ? (gain / t.cost) * 100 : 0;
              const hasValue = t || cash !== 0;
              const netValue = (t ? t.value : 0) + cash;
              return (
              <tr key={a.id}>
                <td>
                  <strong
                    style={{ cursor: 'pointer', color: 'var(--accent)' }}
                    onClick={() => onNavigate && onNavigate('positions', { accountId: a.id })}
                  >
                    {a.name}
                  </strong>
                </td>
                <td><span className="muted">{a.account_type}</span></td>
                <td>{a.institution}</td>
                <td className="amount" style={{whiteSpace:'nowrap'}}>
                  {cash < 0 && (
                    <span className="loss" style={{ marginRight: 6, verticalAlign: 'middle' }}
                      title="Negative cash — add an opening balance (or missing deposits) for this account">
                      <WarningIcon width={14} height={14} />
                    </span>
                  )}
                  <span
                    className={`mono ${cash < 0 ? 'loss' : ''}`}
                    style={{ cursor: 'pointer' }}
                    title="View cash entries"
                    onClick={() => onNavigate && onNavigate('transactions', { tab: 'cash', account_id: a.id })}
                  >
                    {fmtSigned(cash)}
                  </span>
                  {c && !c.has_opening_balance && (
                    <div>
                      <button className="btn-ghost" style={{ fontSize: 11, padding: '2px 6px', marginTop: 4 }} onClick={() => setOpeningFor(a)}>
                        Set opening balance
                      </button>
                    </div>
                  )}
                </td>
                <td className="amount">
                  {netValue < 0 && (
                    <span className="loss" style={{ marginRight: 6, verticalAlign: 'middle' }}
                      title="Net value is below zero — add the missing deposit, sell or opening balance for this account">
                      <WarningIcon width={14} height={14} />
                    </span>
                  )}
                  {hasValue ? (
                    <span
                      className={`mono ${gain >= 0 ? 'gain' : 'loss'}`}
                      title={t
                        ? `Holdings ${fmt(t.value)} + cash ${fmtSigned(cash)}\n${gain >= 0 ? '+' : '-'}${fmt(Math.abs(gain))} (${fmtPct(pct)}) vs. cost ${fmt(t.cost)}`
                        : `Cash ${fmtSigned(cash)}`}
                    >
                      {fmtSigned(netValue)}
                    </span>
                  ) : <span className="muted">—</span>}
                </td>
                <td style={{textAlign:'right'}}>
                  <button className="icon-btn" title="Delete" aria-label="Delete" onClick={() => remove(a.id)}><DeleteIcon /></button>
                </td>
              </tr>
              );
            })}
            {accounts.length === 0 && (
              <tr><td colSpan={6} className="muted" style={{textAlign:'center', padding: 32}}>No accounts yet.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-title">Add Account</div>
            <div className="form-group">
              <label>Account Name</label>
              <input value={form.name} onChange={e => setForm({...form, name: e.target.value})} placeholder="e.g. Fidelity Roth IRA" />
            </div>
            <div className="form-group">
              <label>Type</label>
              <select value={form.account_type} onChange={e => setForm({...form, account_type: e.target.value})}>
                {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Institution</label>
              <select value={form.institution} onChange={e => setForm({...form, institution: e.target.value})}>
                {INSTITUTIONS.map(i => <option key={i} value={i}>{i}</option>)}
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div className="form-group">
                <label>Opening Cash</label>
                <input type="number" min="0" step="0.01" value={form.opening_cash} onChange={e => setForm({...form, opening_cash: e.target.value})} placeholder="0.00" />
              </div>
              <div className="form-group">
                <label>As Of</label>
                <input type="date" value={form.opening_cash_date} onChange={e => setForm({...form, opening_cash_date: e.target.value})} />
              </div>
            </div>
            <div className="muted" style={{ fontSize: 12, marginBottom: 12 }}>
              Cash in the account before any trades you'll record (0 if it starts empty). Trades can't be dated before it.
            </div>
            {formError && <div className="loss" style={{ fontSize: 12, marginBottom: 12 }}>{formError}</div>}
            <div className="modal-actions">
              <button className="btn-ghost" onClick={() => setShowModal(false)}>Cancel</button>
              <button className="btn" onClick={submit}>Save</button>
            </div>
          </div>
        </div>
      )}

      {openingFor && (
        <CashEntryModal
          entry={{
            account_id: openingFor.id,
            type: 'opening_balance',
            amount: openingSuggestion ? openingSuggestion.amount : '',
            date: openingSuggestion ? openingSuggestion.date : undefined,
          }}
          accounts={accounts}
          balances={cashBalances}
          lockAccount
          hint={openingSuggestion
            ? `Prefilled with the smallest balance that keeps cash from going negative, dated at the account's first activity (${openingSuggestion.date}). Adjust to match your statement.`
            : 'Enter the cash the account held before its first recorded trade.'}
          onClose={() => setOpeningFor(null)}
          onSaved={() => { setOpeningFor(null); load(); }}
        />
      )}
    </div>
  );
}
